import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { api, apiDownload, apiUpload, type ListResult } from '@/lib/api-client';
import type {
  Appointment,
  AppointmentCreateBody,
  AppointmentPatchBody,
  EntityValuesUpdateBody,
  QuoteCalculateBody,
  QuoteCalculateResponse,
  QuoteCreateBody,
  QuoteSuggestBody,
  QuoteSuggestResponse,
  TicketCharge,
  TicketChargeCreateBody,
  TicketCloseBody,
  TicketComment,
  TicketCommentBody,
  TicketCreateBody,
  TicketDetail,
  TicketFile,
  TicketListItem,
  TicketPatchBody,
  TicketQuote,
} from '@erp/shared';

const TICKETS_KEY = ['erp', 'tickets'] as const;

export function useCreateTicket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TicketCreateBody) => {
      const res = await api<TicketDetail>('/tickets', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

export function usePatchTicket(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TicketPatchBody) => {
      const res = await api<TicketDetail>(`/tickets/${ticketNumber}`, { method: 'PATCH', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

/**
 * POST /tickets/:n/mirror-detach (spec 024, admin) — tira o ticket do espelho
 * do TiFlux: passa a ser operado no ERP, e o espelho não o toca mais.
 */
export function useDetachTicketFromMirror(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api<TicketDetail>(`/tickets/${ticketNumber}/mirror-detach`, { method: 'POST' });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

export type TicketAction = 'close' | 'cancel' | 'reopen';

export function useTicketAction(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (action: TicketAction) => {
      const res = await api<TicketDetail>(`/tickets/${ticketNumber}/${action}`, { method: 'POST' });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

/**
 * POST /tickets/:n/close com o motivo e a observação (spec 054). Na mesa com
 * motivo ativo a API recusa sem motivo (close_reason_required).
 */
export function useCloseTicket(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TicketCloseBody) => {
      const res = await api<TicketDetail>(`/tickets/${ticketNumber}/close`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

/** POST /tickets/:n/sla/pause | resume — pausa/retomada do SLA (v3). */
export function useSlaAction(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (action: 'pause' | 'resume') => {
      const res = await api<TicketDetail>(`/tickets/${ticketNumber}/sla/${action}`, {
        method: 'POST',
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

// ── Anexos (v3) — mesmo limite e mesmo fluxo autenticado da KB ──────────────

/** Upload multipart (campo 'file') via helper compartilhado apiUpload. */
export function uploadTicketFile(ticketNumber: number, file: File): Promise<TicketFile> {
  return apiUpload<TicketFile>(`/tickets/${ticketNumber}/files`, file);
}

export function useUploadTicketFile(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => uploadTicketFile(ticketNumber, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

export function useDeleteTicketFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (fileId: number) => {
      const res = await api<void>(`/tickets/files/${fileId}`, { method: 'DELETE' });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

/** Download autenticado (fetch + blob + <a download>) via helper apiDownload. */
export function downloadTicketFile(file: TicketFile): Promise<void> {
  return apiDownload(`/tickets/files/${file.id}`, file.fileName);
}

export function useAddComment(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TicketCommentBody) => {
      const res = await api<TicketComment>(`/tickets/${ticketNumber}/comments`, {
        method: 'POST',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

export function useCreateAppointment(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: AppointmentCreateBody) => {
      const res = await api<Appointment>(`/tickets/${ticketNumber}/appointments`, {
        method: 'POST',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

/**
 * PATCH /tickets/:n/appointments/:id (pedido da PoolTrack, 02/10). Invalida
 * tudo do ticket: a tabela, os cards (Horas/Apontamentos, do detalhe) e o
 * Histórico (o evento appointment_edited) vêm de queries diferentes.
 */
export function useEditAppointment(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, body }: { id: number; body: AppointmentPatchBody }) => {
      const res = await api<Appointment>(`/tickets/${ticketNumber}/appointments/${id}`, {
        method: 'PATCH',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

export function useDeleteAppointment(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (appointmentId: number) => {
      const res = await api<void>(`/tickets/${ticketNumber}/appointments/${appointmentId}`, {
        method: 'DELETE',
      });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

// ── Lançamentos financeiros do ticket (v9) ──────────────────────────────────

export function useCreateTicketCharge(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: TicketChargeCreateBody) => {
      const res = await api<TicketCharge>(`/tickets/${ticketNumber}/charges`, {
        method: 'POST',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

export function useDeleteTicketCharge(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (chargeId: number) => {
      const res = await api<void>(`/tickets/${ticketNumber}/charges/${chargeId}`, {
        method: 'DELETE',
      });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

// ── Cotação de deslocamento (PoolTrack, 04/10) ──────────────────────────────

/**
 * "Calcular": geocodificação + rotas no servidor, sem gravar nada — por isso
 * não invalida cache. Cada chamada é cobrada pelo provedor: a tela só chama no
 * botão Calcular e no fim de um arraste no mapa, nunca a cada tecla.
 */
export function useCalculateQuote(ticketNumber: number) {
  return useMutation({
    mutationFn: async (body: QuoteCalculateBody) => {
      const res = await api<QuoteCalculateResponse>(`/tickets/${ticketNumber}/quotes/calculate`, {
        method: 'POST',
        body,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });
}

/**
 * Sugestões do autocompletar de endereço (05/10). Não é query do React Query:
 * cada consulta é cobrada, e quem decide quando consultar (pausa de digitação,
 * cancelamento da anterior) é o campo. Qualquer erro vira `unavailable` — a
 * lista some e o campo segue como texto livre.
 */
export async function fetchQuoteSuggestions(
  ticketNumber: number,
  body: QuoteSuggestBody,
  signal: AbortSignal
): Promise<QuoteSuggestResponse> {
  const res = await api<QuoteSuggestResponse>(`/tickets/${ticketNumber}/quotes/suggest`, {
    method: 'POST',
    body,
    signal,
  });
  return res.error ? { status: 'unavailable' } : res.data;
}

/** "Lançar": grava a cotação e os lançamentos — invalida valores e cotações. */
export function useCreateQuote(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: QuoteCreateBody) => {
      const res = await api<TicketQuote>(`/tickets/${ticketNumber}/quotes`, { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

export function usePlayTimer(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await api<Appointment>(`/tickets/${ticketNumber}/appointments/play`, {
        method: 'POST',
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

export function useStopTimer(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (description: string | null) => {
      const res = await api<Appointment>(`/tickets/${ticketNumber}/appointments/stop`, {
        method: 'POST',
        body: { description },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

/**
 * Kanban drag-and-drop: PATCH stage with an optimistic update of the board list
 * cache (rollback on error, refetch on settle).
 */
export function useMoveTicketStage(listKey: QueryKey) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      ticketNumber,
      stageId,
    }: {
      ticketNumber: number;
      stageId: number;
      stageName: string;
    }) => {
      const res = await api<TicketDetail>(`/tickets/${ticketNumber}`, {
        method: 'PATCH',
        body: { stageId },
      });
      if (res.error) throw res.error;
      return res.data;
    },
    onMutate: async ({ ticketNumber, stageId, stageName }) => {
      await qc.cancelQueries({ queryKey: listKey });
      const previous = qc.getQueryData<ListResult<TicketListItem>>(listKey);
      if (previous) {
        qc.setQueryData<ListResult<TicketListItem>>(listKey, {
          ...previous,
          items: previous.items.map((t) =>
            t.ticketNumber === ticketNumber ? { ...t, stage: { id: stageId, name: stageName } } : t
          ),
        });
      }
      return { previous };
    },
    onError: (_error, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(listKey, ctx.previous);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}

/** PUT dos campos personalizados do ticket (Informações adicionais). */
export function useUpdateTicketEntities(ticketNumber: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: EntityValuesUpdateBody) => {
      const res = await api<unknown>(`/tickets/${ticketNumber}/entities`, {
        method: 'PUT',
        body,
      });
      if (res.error) throw res.error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TICKETS_KEY }),
  });
}
